import palette from '@assets/theme/palette';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  checkEmptyInputValue,
  getFormattedFloatNumber,
} from '@pages/AdditionalServices/tabs/DeliveryForm/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomCargoCalcType, SelectionCustomCargoVolumeParameters } from '../types';
import { CargoSelectionItemProps } from './types';

function CargoSelectionVolume({
  selectionCargoParamaters,
  position,
  isActionsDisabled,
  isDeletable,
  onChangeSelectionCargoParameters,
  onDeleteSelectionCargoParameters,
}: CargoSelectionItemProps) {
  const { t } = useTranslation('Common');

  const textColor = palette.light.grey['700'];

  const isVolumeInputInvalid = checkEmptyInputValue(
    selectionCargoParamaters.volumeParameters.volume
  );
  const isWeightInputInvalid = checkEmptyInputValue(
    selectionCargoParamaters.volumeParameters.weight
  );

  const handleChangeCargoSelectionVolumeParameters = <
    T extends keyof SelectionCustomCargoVolumeParameters
  >(
    key: T,
    value: SelectionCustomCargoVolumeParameters[T]
  ) => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      volumeParameters: {
        ...selectionCargoParamaters.volumeParameters,
        [key]: value,
      },
    });
  };

  const handleChangeCustomCargoCalcType = (type: CustomCargoCalcType) => {
    onChangeSelectionCargoParameters({
      ...selectionCargoParamaters,
      parametersCargoCalcType: type,
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Typography variant={'subtitle2'} color={'#707070'}>
        {t('Common:CargoPlace')} #{position}
      </Typography>
      <Box className={'cargo-types'}>
        <Typography
          className={'cargo-types__title'}
          component={'span'}
          variant={'body2'}
          sx={{ color: textColor, fontSize: '12px' }}
        >
          {t('Common:CalculationType')}
        </Typography>
        <Button disabled={isActionsDisabled} size={'small'} variant={'outlined'}>
          {t('Common:Consignment')}
        </Button>
        <Button
          size={'small'}
          variant={'outlined'}
          className={'not-selected'}
          color={'inherit'}
          disabled={isActionsDisabled}
          onClick={() => handleChangeCustomCargoCalcType('base')}
        >
          {t('Common:CargoUnit')}
        </Button>
      </Box>
      <Box className={'cargo-inputs-block'}>
        <TextField
          error={isVolumeInputInvalid}
          focused={isVolumeInputInvalid}
          fullWidth
          variant={'standard'}
          disabled={isActionsDisabled}
          value={selectionCargoParamaters.volumeParameters.volume}
          onFocus={() =>
            handleChangeCargoSelectionVolumeParameters('volume', getFormattedFloatNumber(''))
          }
          onChange={(event) =>
            handleChangeCargoSelectionVolumeParameters(
              'volume',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:VolumeM3')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          error={isWeightInputInvalid}
          focused={isWeightInputInvalid}
          fullWidth
          variant={'standard'}
          disabled={isActionsDisabled}
          value={selectionCargoParamaters.volumeParameters.weight}
          onFocus={() =>
            handleChangeCargoSelectionVolumeParameters('weight', getFormattedFloatNumber(''))
          }
          onChange={(event) =>
            handleChangeCargoSelectionVolumeParameters(
              'weight',
              getFormattedFloatNumber(event.target.value)
            )
          }
          type={'text'}
          InputProps={{
            inputMode: 'numeric',
            startAdornment: (
              <InputAdornment
                sx={{ color: '#707070', position: 'absolute', pointerEvents: 'none' }}
                position="start"
              >
                <Typography component={'span'} variant={'h5'}>
                  {t('Common:ConsignmentKg')}
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {isDeletable && (
        <IconButton
          sx={{
            position: 'absolute',
            right: '-15px',
            top: '-15px',
          }}
          disabled={isActionsDisabled}
          size={'small'}
          onClick={onDeleteSelectionCargoParameters}
        >
          <CloseIcon sx={{ fontSize: 22 }} />
        </IconButton>
      )}
    </Box>
  );
}

export default CargoSelectionVolume;
